import StyledSwitch from '@/components/common/StyledSwitch'
import Layout from '@/components/layout/Layout'
import CustomButton from '@/components/Returns/CustomButton'
import { useSidebar } from '@/contexts/SidebarContext'
import { useSnackbar } from '@/contexts/SnackbarProvider'
import { MTCustomSelect as CustomSelectBase } from '@/multi-territory/mt-components/mt-shared/MTCustomSelect'
import {
    createRule,
    fetchRuleConditions,
    fetchServiceMasterValues,
    updateRule,
    type RuleCondition,
    type RuleCreateRequest,
    type RuleUpdateRequest,
} from '@/multi-territory/mt-services/mt-rule-setting-api'
import { ROUTES } from '@/routes/routeConfig'
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material'
import {
    Box,
    Button,
    IconButton,
    Paper,
    TextField,
    Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/en-gb'
import { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router'
const CustomSelect = CustomSelectBase as any

const FIELD_OPTIONS = [
  { value: 'NATURE_OF_PAYMENT', label: 'Nature of Payment' },
  { value: 'INVOICE_AMOUNT_IN_S', label: 'Invoice Amount (SGD)' },
  { value: 'PAYEE_NAME', label: 'Payee Name' },
  { value: 'PO_DESCRIPTION', label: 'PO Description' },
  { value: 'VENDOR_NAME', label: 'Vendor Name' },
  { value: 'TRANSACTION_CATEGORY', label: 'Transaction Category' },
  { value: 'GROSS_AMOUNT', label: 'Gross Amount' },
]

const OPERATOR_OPTIONS = [
  { value: 'EQUAL_TO', label: 'Equal To' },
  { value: 'NOT_EQUAL_TO', label: 'Not Equal To' },
  { value: 'GREATER_THAN', label: 'Greater Than' },
  { value: 'LESS_THAN', label: 'Less Than' },
  { value: 'GREATER_THAN_OR_EQUAL_TO', label: 'Greater Than Or Equal' },
  { value: 'LESS_THAN_OR_EQUAL_TO', label: 'Less Than Or Equal' },
  { value: 'DOES_NOT_CONTAIN', label: 'Does Not Contain' },
  { value: 'LIKE', label: 'Like' },
  { value: 'STARTS_WITH', label: 'Starts With' },
  { value: 'ENDS_WITH', label: 'Ends With' },
  { value: 'IS_NULL', label: 'Is Null' },
  { value: 'IS_NOT_NULL', label: 'Is Not Null' },
  { value: 'IN_LIST', label: 'In List' },
  { value: 'NOT_IN_LIST', label: 'Not In List' },
  { value: 'BETWEEN', label: 'Between' },
]

const LOGICAL_OPERATOR_OPTIONS = [
  { value: 'AND', label: 'AND' },
  { value: 'OR', label: 'OR' },
]

interface FormCondition {
  id: number
  conditionId?: string
  field: string
  operator: string
  value: string
  logicalOperator: 'AND' | 'OR'
}

export function MTRuleSettingFormPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editUuid = searchParams.get('edit')
  const isEditMode = !!editUuid
  const { showSnackbar } = useSnackbar()
  const { isCollapsed } = useSidebar()

  const countryCode = useSelector(
    (state: { country: { countryCode: string } }) => state.country?.countryCode
  )
  const selectedEntityUuid = useSelector(
    (state: any) => state.mtEntityTan?.selectedEntityUuid ?? ''
  )
  const selectedTanUuid = useSelector(
    (state: any) => state.mtEntityTan?.selectedTanUuid ?? ''
  )
  const entityMetaByUuid = useSelector(
    (state: any) => state.mtEntityTan?.entityMetaByUuid ?? {}
  )

  // Derive tanApplicable from entity metadata
  const selectedEntityMeta = entityMetaByUuid[selectedEntityUuid] || null
  const tanApplicable = selectedEntityMeta?.tanApplicable ?? true

  /**
   * API identifier logic:
   * - tanApplicable=true  -> use selectedTanUuid (tanMasterUuid)
   * - tanApplicable=false -> use selectedEntityUuid (entityMasterUuid)
   */
  const apiIdentifierUuid = tanApplicable ? selectedTanUuid : selectedEntityUuid

  const [ruleName, setRuleName] = useState('')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [active, setActive] = useState(true)
  const [conditions, setConditions] = useState<FormCondition[]>([
    { id: 1, field: '', operator: '', value: '', logicalOperator: 'AND' },
  ])
  const [natureOfService, setNatureOfService] = useState('')
  const [natureOfServiceOptions, setNatureOfServiceOptions] = useState<
    { value: string; label: string }[]
  >([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [loadingConditions, setLoadingConditions] = useState(false)

  const getConditionKey = (conditionId: number, suffix: string) =>
    'condition_' + conditionId + '_' + suffix

  // --- Fetch Nature of Service options from API ---
  const loadServiceMasterValues = useCallback(async () => {
    if (!countryCode) return
    const result = await fetchServiceMasterValues(countryCode)
    if (result.success && Array.isArray(result.data)) {
      setNatureOfServiceOptions(
        result.data.map((val: string) => ({ value: val, label: val }))
      )
    }
  }, [countryCode])

  useEffect(() => {
    loadServiceMasterValues()
  }, [loadServiceMasterValues])

  // --- Fetch rule conditions in edit mode ---
  const loadRuleConditions = useCallback(async () => {
    if (!editUuid) return
    setLoadingConditions(true)
    const result = await fetchRuleConditions(editUuid)
    if (result.success && Array.isArray(result.data)) {
      const formConditions: FormCondition[] = result.data
        .sort(
          (a: RuleCondition, b: RuleCondition) =>
            ((a as any).conditionOrder ?? 0) - ((b as any).conditionOrder ?? 0)
        )
        .map((c: RuleCondition, idx: number) => ({
          id: idx + 1,
          conditionId: c.conditionId,
          field: c.conditionName,
          operator: c.operatorName,
          value: c.conditionValue,
          logicalOperator: (c.logicalOperator as 'AND' | 'OR') || 'AND',
        }))
      if (formConditions.length > 0) {
        setConditions(formConditions)
      }
    } else {
      showSnackbar(result.error ?? 'Failed to load rule conditions', 'error')
    }
    setLoadingConditions(false)
  }, [editUuid, showSnackbar])

  useEffect(() => {
    if (isEditMode) {
      loadRuleConditions()
    }
  }, [isEditMode, loadRuleConditions])

  // --- Condition management ---
  const addCondition = () => {
    const newId = Math.max(0, ...conditions.map(c => c.id)) + 1
    setConditions(prev => [
      ...prev,
      { id: newId, field: '', operator: '', value: '', logicalOperator: 'AND' },
    ])
  }

  const removeCondition = (conditionId: number) => {
    if (conditions.length <= 1) return
    setConditions(prev => prev.filter(c => c.id !== conditionId))
  }

  const handleConditionChange = (
    conditionId: number,
    field: string,
    value: string
  ) => {
    // When switching to a null-check operator, the value field is disabled;
    // clear any previously entered value so a stale value/error does not linger.
    const isNullOperator =
      field === 'operator' && (value === 'IS_NULL' || value === 'IS_NOT_NULL')

    setConditions(prev =>
      prev.map(c =>
        c.id === conditionId
          ? { ...c, [field]: value, ...(isNullOperator ? { value: '' } : {}) }
          : c
      )
    )
    const key = getConditionKey(conditionId, field)
    const valueKey = getConditionKey(conditionId, 'value')
    setErrors(prev => {
      const next = { ...prev }
      delete next[key]
      if (isNullOperator) delete next[valueKey]
      return next
    })
  }

  // --- Validation ---
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!ruleName) newErrors.ruleName = 'Rule name is required'
    if (!startDate) newErrors.startDate = 'Start date is required'
    if (!natureOfService)
      newErrors.natureOfService = 'Nature of service is required'
    if (startDate && endDate) {
      const s = dayjs(startDate, 'YYYY-MM-DD')
      const e = dayjs(endDate, 'YYYY-MM-DD')
      if (e.isBefore(s)) newErrors.endDate = 'End date must be after start date'
    }
    conditions.forEach(c => {
      if (!c.field)
        newErrors[getConditionKey(c.id, 'field')] = 'Field is required'
      if (!c.operator)
        newErrors[getConditionKey(c.id, 'operator')] = 'Operator is required'
      if (
        !c.value &&
        c.operator !== 'IS_NULL' &&
        c.operator !== 'IS_NOT_NULL'
      ) {
        newErrors[getConditionKey(c.id, 'value')] = 'Value is required'
      }
    })
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // --- Submit: create or update ---
  const handleSubmit = async () => {
    if (!validate()) return
    if (!countryCode || !apiIdentifierUuid) {
      showSnackbar('Please select an entity and TAN first', 'error')
      return
    }

    setSubmitting(true)

    // Build identifier fields based on tanApplicable
    const identifierFields = tanApplicable
      ? { tanMasterUuid: apiIdentifierUuid, entityMasterUuid: null }
      : { entityMasterUuid: apiIdentifierUuid, tanMasterUuid: null }

    const conditionsPayload: RuleCondition[] = conditions.map(c => ({
      conditionId: c.conditionId,
      conditionName: c.field,
      operatorName: c.operator,
      conditionValue: c.value,
      logicalOperator: c.logicalOperator,
    }))

    if (isEditMode && editUuid) {
      // --- Update rule ---
      const payload: RuleUpdateRequest = {
        ruleId: editUuid,
        ...identifierFields,
        startDate,
        endDate,
        active,
        conditions: conditionsPayload,
      }
      const result = await updateRule(countryCode, payload)
      if (result.success) {
        showSnackbar('Rule updated successfully', 'success')
        navigate(ROUTES.MT_RETURNS_DEFINE + '/RULE_SETTING')
      } else {
        showSnackbar(result.error ?? 'Failed to update rule', 'error')
      }
    } else {
      // --- Create rule ---
      const payload: RuleCreateRequest = {
        ruleName,
        ...identifierFields,
        mappedServiceValue: natureOfService,
        startDate,
        endDate,
        active,
        conditions: conditionsPayload,
      }
      const result = await createRule(countryCode, payload)
      if (result.success) {
        showSnackbar('Rule created successfully', 'success')
        navigate(ROUTES.MT_RETURNS_DEFINE + '/RULE_SETTING')
      } else {
        showSnackbar(result.error ?? 'Failed to create rule', 'error')
      }
    }

    setSubmitting(false)
  }

  const handleCancel = () => {
    navigate(ROUTES.MT_RETURNS_DEFINE + '/RULE_SETTING')
  }

  return (
    <Layout>
      <Box sx={{ pb: '80px', height: '100%', overflowY: 'auto' }}>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              py: '20px',
              px: '32px',
              mx: { xs: -2, sm: -3 },
              mt: { xs: -2, sm: -3 },
              mb: 3,
              backgroundColor: 'var(--bg-white, #FFFFFF)',
              boxShadow: '0px 2px 4px -2px rgba(0, 0, 0, 0.12)',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'charter',
                fontWeight: 600,
                fontSize: 'var(--font-size-xl, 20px)',
                lineHeight: '130%',
                color: 'var(--text-heading, #252525)',
              }}
            >
              {isEditMode ? 'Edit rule' : 'Add new rule'}
            </Typography>
          </Box>

          <LocalizationProvider
            dateAdapter={AdapterDayjs}
            adapterLocale="en-gb"
          >
            <Box sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: 3,
                  mb: 3,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      color: 'var(--forbrand-primary)',
                      mb: 1,
                      lineHeight: '140%',
                    }}
                  >
                    Rule Name
                    <span style={{ color: 'var(--forbrand-error)' }}>*</span>
                  </Typography>
                  <TextField
                    fullWidth
                    value={ruleName}
                    onChange={e => {
                      setRuleName(e.target.value)
                      if (errors.ruleName)
                        setErrors(prev => {
                          const n = { ...prev }
                          delete n.ruleName
                          return n
                        })
                    }}
                    placeholder="Enter rule name"
                    error={!!errors.ruleName}
                    helperText={errors.ruleName}
                    disabled={isEditMode}
                    size="medium"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: isEditMode
                          ? 'var(--bg-disabled)'
                          : '#fff',
                        fontSize: '14px',
                        height: '48px',
                        borderRadius: 'unset',
                        '& fieldset': {
                          borderColor: errors.ruleName
                            ? 'var(--forbrand-error)'
                            : 'var(--forbrand-primary)',
                        },
                      },
                    }}
                    slotProps={{
                      formHelperText: { sx: { margin: 0, mt: 0.5 } },
                      htmlInput: { maxLength: 100 },
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      color: 'var(--forbrand-primary)',
                      mb: 1,
                      lineHeight: '140%',
                    }}
                  >
                    Start Date
                    <span style={{ color: 'var(--forbrand-color)' }}>*</span>
                  </Typography>
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={startDate ? dayjs(startDate, 'YYYY-MM-DD') : null}
                    onChange={newValue => {
                      if (newValue) {
                        setStartDate(newValue.format('YYYY-MM-DD'))
                        if (errors.startDate)
                          setErrors(prev => {
                            const n = { ...prev }
                            delete n.startDate
                            return n
                          })
                      }
                    }}
                    slotProps={
                      {
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          error: !!errors.startDate,
                          helperText: errors.startDate,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 'unset',
                              backgroundColor: 'white',
                              height: '48px',
                              '& fieldset': {
                                borderColor: errors.startDate
                                  ? 'var(--forbrand-error)'
                                  : 'var(--forbrand-primary)',
                              },
                            },
                            '& .MuiFormHelperText-root': { margin: 0, mt: 0.5 },
                          },
                        },
                      } as any
                    }
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      color: 'var(--forbrand-primary)',
                      mb: 1,
                      lineHeight: '140%',
                    }}
                  >
                    End Date
                  </Typography>
                  <DatePicker
                    format="DD/MM/YYYY"
                    value={endDate ? dayjs(endDate, 'YYYY-MM-DD') : null}
                    onChange={newValue => {
                      if (newValue) setEndDate(newValue.format('YYYY-MM-DD'))
                    }}
                    slotProps={
                      {
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          error: !!errors.endDate,
                          helperText: errors.endDate,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 'unset',
                              backgroundColor: 'white',
                              height: '48px',
                              '& fieldset': {
                                borderColor: errors.endDate
                                  ? 'var(--forbrand-error)'
                                  : 'var(--forbrand-primary)',
                              },
                            },
                            '& .MuiFormHelperText-root': { margin: 0, mt: 0.5 },
                          },
                        },
                      } as any
                    }
                  />
                </Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}
                >
                  <Typography
                    sx={{ fontSize: '14px', fontWeight: 500, color: '#333' }}
                  >
                    Active
                  </Typography>
                  <StyledSwitch
                    checked={active}
                    onChange={(e: any) => setActive(e.target.checked)}
                  />
                </Box>
              </Box>
            </Box>
          </LocalizationProvider>

          <Paper
            sx={{ p: { xs: 2, sm: 2 }, mb: 2, backgroundColor: '#FAFAFA' }}
            elevation={0}
          >
            {loadingConditions ? (
              <Typography sx={{ p: 2, color: '#666' }}>
                Loading conditions...
              </Typography>
            ) : (
              <Box>
                {conditions.map((condition, index) => {
                  const fieldKey = getConditionKey(condition.id, 'field')
                  const operatorKey = getConditionKey(condition.id, 'operator')
                  const valueKey = getConditionKey(condition.id, 'value')
                  return (
                    <Box key={condition.id} sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 2,
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            fontSize: '16px',
                            color: '#333',
                          }}
                        >
                          Condition {index + 1}
                        </Typography>
                        {index > 0 && (
                          <CustomSelect
                            label=""
                            placeholder="Logical"
                            options={LOGICAL_OPERATOR_OPTIONS}
                            getOptionLabel={(opt: any) => opt.label}
                            getOptionValue={(opt: any) => opt.value}
                            value={condition.logicalOperator}
                            onChange={(e: any) =>
                              handleConditionChange(
                                condition.id,
                                'logicalOperator',
                                e.target.value
                              )
                            }
                            sx={{ width: 100 }}
                          />
                        )}
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', md: 'row' },
                          gap: 2,
                          alignItems: { xs: 'flex-start', sm: 'flex-end' },
                        }}
                      >
                        <CustomSelect
                          label="Field"
                          placeholder="Select Field"
                          options={FIELD_OPTIONS}
                          getOptionLabel={(opt: any) => opt.label}
                          getOptionValue={(opt: any) => opt.value}
                          value={condition.field || ''}
                          onChange={(e: any) =>
                            handleConditionChange(
                              condition.id,
                              'field',
                              e.target.value
                            )
                          }
                          error={!!errors[fieldKey]}
                          helperText={errors[fieldKey]}
                          fullWidth
                        />
                        <CustomSelect
                          label="Operator"
                          placeholder="Select Operator"
                          options={OPERATOR_OPTIONS}
                          getOptionLabel={(opt: any) => opt.label}
                          getOptionValue={(opt: any) => opt.value}
                          value={condition.operator || ''}
                          onChange={(e: any) =>
                            handleConditionChange(
                              condition.id,
                              'operator',
                              e.target.value
                            )
                          }
                          error={!!errors[operatorKey]}
                          helperText={errors[operatorKey]}
                          fullWidth
                        />
                        <Box sx={{ flex: 1, minWidth: 225, width: '100%' }}>
                          <Typography
                            sx={{
                              fontSize: '16px',
                              fontWeight: 500,
                              color: 'var(--forbrand-primary)',
                              mb: 1,
                              lineHeight: '140%',
                            }}
                          >
                            Value
                          </Typography>
                          <TextField
                            fullWidth
                            size="medium"
                            value={condition.value}
                            onChange={e =>
                              handleConditionChange(
                                condition.id,
                                'value',
                                e.target.value
                              )
                            }
                            placeholder="Enter value"
                            disabled={
                              condition.operator === 'IS_NULL' ||
                              condition.operator === 'IS_NOT_NULL'
                            }
                            error={!!errors[valueKey]}
                            helperText={errors[valueKey]}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                height: '48px',
                                fontSize: '14px',
                                borderRadius: 'unset',
                                backgroundColor:
                                  condition.operator === 'IS_NULL' ||
                                  condition.operator === 'IS_NOT_NULL'
                                    ? 'var(--bg-disabled)'
                                    : '#fff',
                                '& fieldset': {
                                  borderColor: errors[valueKey]
                                    ? 'var(--forbrand-error)'
                                    : '#d0d0d0',
                                },
                              },
                            }}
                            slotProps={{
                              formHelperText: { sx: { margin: 0, mt: 0.5 } },
                            }}
                          />
                        </Box>
                        {conditions.length > 1 && (
                          <IconButton
                            onClick={() => removeCondition(condition.id)}
                            sx={{
                              color: 'var(--forbrand-error)',
                              border: '1px solid #EF4444',
                              borderRadius: '4px',
                              mt: { xs: 0, md: '32px' },
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  )
                })}
              </Box>
            )}
          </Paper>

          <Button
            startIcon={<AddIcon />}
            onClick={addCondition}
            sx={{
              color: '#EF4444',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              mb: 1,
            }}
          >
            Add Condition
          </Button>

          <Box sx={{ mb: 6, width: { xs: '100%', md: '40%' } }}>
            <CustomSelect
              label="Nature of Service"
              placeholder="Select nature of service"
              options={natureOfServiceOptions}
              getOptionLabel={(opt: any) => opt.label}
              getOptionValue={(opt: any) => opt.value}
              value={natureOfService || ''}
              onChange={(e: any) => {
                setNatureOfService(e.target.value)
                if (errors.natureOfService)
                  setErrors(prev => {
                    const n = { ...prev }
                    delete n.natureOfService
                    return n
                  })
              }}
              error={!!errors.natureOfService}
              helperText={errors.natureOfService}
              required
              disabled={isEditMode}
              fullWidth
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: { xs: 0, lg: isCollapsed ? '66px' : '300px' },
          right: 0,
          backgroundColor: '#fff',
          borderTop: '1px solid var(--border-subtel)',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.05)',
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 1.5,
          zIndex: 1,
          transition: 'left 0.3s ease',
        }}
      >
        <Button
          variant="outlined"
          onClick={handleCancel}
          sx={{
            textTransform: 'none',
            fontSize: '14px',
            color: 'var(--forbrand-primary)',
            borderColor: 'var(--border-subtel)',
            borderRadius: '4px',
            minWidth: '80px',
            height: '36px',
          }}
        >
          Cancel
        </Button>
        <CustomButton onClick={handleSubmit} size="small" disabled={submitting}>
          {submitting ? 'Saving...' : 'Save'}
        </CustomButton>
      </Box>
    </Layout>
  )
}
